// Global receiver for superadmin broadcast announcements — mounted once in
// App.js, so every role (superadmin, admin, manager, user) gets them.
//
// Sources of truth, in order of speed:
//   1. `hrms:broadcast:new` / `hrms:broadcast:revoked` window events, re-emitted
//      by NotificationContext's always-on socket (same bridge pattern as DMs).
//   2. GET /api/broadcasts/active — on mount, on tab focus and every 60s. This
//      covers offline users, accounts without an infinityCode (no socket room),
//      and anything a socket reconnect missed.
//
// Display: MODAL (one at a time, most severe first) > BANNER stack (top) >
// TOAST (corner, auto-hide). Seen / acknowledge / dismiss go back to the API.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../../context/AuthContext";
import { LanguageContext } from "../../constant/LanguageContext";
import { ThemeContext } from "../../constant/theme";
import BroadcastCard from "./BroadcastCard";
import { SEVERITY_RANK, BROADCAST_STRINGS } from "./broadcastTheme";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const HIDDEN_ON_ROUTES = ["/", "/login", "/reset-password"];
const POLL_MS = 60000;
const TOAST_MS = 9000;
const MAX_VISIBLE_BANNERS = 2;

const bySeverityThenNewest = (a, b) =>
  (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) ||
  new Date(b.sentAt || 0) - new Date(a.sentAt || 0);

const isExpired = (b) => b.expiresAt && new Date(b.expiresAt).getTime() <= Date.now();

const BroadcastCenter = () => {
  const location = useLocation();
  const { language } = useContext(LanguageContext) || { language: "en" };
  const { theme } = useContext(ThemeContext) || { theme: "light" };
  const t = BROADCAST_STRINGS[language === "ar" ? "ar" : "en"];

  const [items, setItems] = useState([]);
  const [showAllBanners, setShowAllBanners] = useState(false);
  // Hidden for this session only (non-dismissible modal closed, toast timed out).
  const [locallyHidden, setLocallyHidden] = useState(() => new Set());
  const seenSent = useRef(new Set());

  const hiddenRoute = HIDDEN_ON_ROUTES.includes(location.pathname);
  const hasToken = () => {
    try {
      return !!localStorage.getItem("token");
    } catch {
      return false;
    }
  };

  const upsert = useCallback((incoming) => {
    setItems((prev) => {
      const map = new Map(prev.map((b) => [b.id, b]));
      for (const b of incoming) map.set(b.id, { ...map.get(b.id), ...b });
      return [...map.values()].filter((b) => !isExpired(b));
    });
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((b) => b.id !== id)), []);

  const fetchActive = useCallback(async () => {
    if (!hasToken()) return;
    try {
      const res = await apiFetch(`${BASE_URL}/api/broadcasts/active`, { method: "GET", reportConnection: false });
      if (!res.ok) return;
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : [];
      // Server list is authoritative: replace, so retracted/expired ones disappear.
      setItems(list.filter((b) => !isExpired(b)));
    } catch {
      /* offline — the next poll or socket event will catch up */
    }
  }, []);

  const post = useCallback((id, action) => {
    return apiFetch(`${BASE_URL}/api/broadcasts/${id}/${action}`, { method: "POST", reportConnection: false }).catch(() => {});
  }, []);

  // Initial load, focus refresh, polling.
  useEffect(() => {
    if (hiddenRoute) return undefined;
    fetchActive();
    const onFocus = () => fetchActive();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(fetchActive, POLL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [hiddenRoute, fetchActive]);

  // Live socket bridge + logout cleanup.
  useEffect(() => {
    const onNew = (e) => e.detail && upsert([e.detail]);
    const onRevoked = (e) => e.detail?.id != null && remove(e.detail.id);
    const onLogout = () => {
      setItems([]);
      seenSent.current = new Set();
      setLocallyHidden(new Set());
    };
    window.addEventListener("hrms:broadcast:new", onNew);
    window.addEventListener("hrms:broadcast:revoked", onRevoked);
    window.addEventListener("hrms:auth:logout", onLogout);
    return () => {
      window.removeEventListener("hrms:broadcast:new", onNew);
      window.removeEventListener("hrms:broadcast:revoked", onRevoked);
      window.removeEventListener("hrms:auth:logout", onLogout);
    };
  }, [upsert, remove]);

  // Drop items the moment they expire, without waiting for the next poll.
  useEffect(() => {
    const next = items
      .map((b) => (b.expiresAt ? new Date(b.expiresAt).getTime() - Date.now() : Infinity))
      .filter((ms) => ms > 0 && Number.isFinite(ms));
    if (!next.length) return undefined;
    const timer = setTimeout(() => setItems((prev) => prev.filter((b) => !isExpired(b))), Math.min(...next) + 500);
    return () => clearTimeout(timer);
  }, [items]);

  const visible = useMemo(
    () => (hiddenRoute ? [] : items.filter((b) => !locallyHidden.has(b.id)).sort(bySeverityThenNewest)),
    [items, locallyHidden, hiddenRoute]
  );
  // Memoized so the effects below only re-run when the set actually changes
  // (a re-render must not restart toast timers).
  const modal = useMemo(() => visible.find((b) => b.displayMode === "MODAL") || null, [visible]);
  const banners = useMemo(() => visible.filter((b) => b.displayMode === "BANNER"), [visible]);
  const toasts = useMemo(() => visible.filter((b) => b.displayMode === "TOAST").slice(0, 3), [visible]);
  const toastKey = toasts.map((b) => `${b.id}:${b.severity}`).join(",");

  // Report "seen" once per broadcast when it is actually on screen.
  useEffect(() => {
    const onScreen = [modal, ...banners.slice(0, showAllBanners ? banners.length : MAX_VISIBLE_BANNERS), ...toasts].filter(Boolean);
    for (const b of onScreen) {
      if (b.readAt || seenSent.current.has(b.id)) continue;
      seenSent.current.add(b.id);
      post(b.id, "seen");
    }
  }, [modal, banners, toasts, showAllBanners, post]);

  // Toasts auto-hide (CRITICAL stays until closed).
  useEffect(() => {
    const timers = toasts
      .filter((b) => b.severity !== "CRITICAL")
      .map((b) => setTimeout(() => setLocallyHidden((s) => new Set(s).add(b.id)), TOAST_MS));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastKey]);

  const dismiss = (b) => {
    remove(b.id);
    post(b.id, "dismiss");
  };
  const ack = (b) => {
    remove(b.id);
    post(b.id, "ack");
  };
  const hideLocally = (b) => setLocallyHidden((s) => new Set(s).add(b.id));

  if (hiddenRoute || visible.length === 0) return null;

  const rtl = language === "ar";
  const shownBanners = showAllBanners ? banners : banners.slice(0, MAX_VISIBLE_BANNERS);

  return (
    <>
      <style>{"@media (max-width: 768px) { .broadcast-banner-stack { top: calc(3.5rem + 8px) !important; } }"}</style>
      {banners.length > 0 && (
        <div
          className="broadcast-banner-stack"
          style={{
            position: "fixed",
            // Just below the app topbar (4rem; 3.5rem at ≤768px — see
            // sidebarStyles.topbarHeight) so it never covers its controls.
            top: "calc(4rem + 10px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(760px, calc(100vw - 24px))",
            zIndex: 9000,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            pointerEvents: "none",
          }}
        >
          {shownBanners.map((b) => (
            <div key={b.id} style={{ pointerEvents: "auto" }}>
              <BroadcastCard
                broadcast={b}
                language={language}
                theme={theme}
                variant="banner"
                onDismiss={() => dismiss(b)}
                onAck={() => ack(b)}
              />
            </div>
          ))}
          {banners.length > MAX_VISIBLE_BANNERS && (
            <button
              type="button"
              onClick={() => setShowAllBanners((v) => !v)}
              style={{
                pointerEvents: "auto",
                alignSelf: "center",
                border: "none",
                borderRadius: 999,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                background: theme === "dark" ? "rgba(30,41,59,0.95)" : "rgba(255,255,255,0.95)",
                color: theme === "dark" ? "#cbd5e1" : "#475569",
                boxShadow: "0 4px 12px rgba(15,23,42,0.12)",
              }}
            >
              {showAllBanners ? "−" : t.more(banners.length - MAX_VISIBLE_BANNERS)}
            </button>
          )}
        </div>
      )}

      {toasts.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            [rtl ? "right" : "left"]: 24,
            width: "min(380px, calc(100vw - 32px))",
            zIndex: 9001,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {toasts.map((b) => (
            <BroadcastCard
              key={b.id}
              broadcast={b}
              language={language}
              theme={theme}
              variant="toast"
              onDismiss={() => dismiss(b)}
              onAck={() => ack(b)}
            />
          ))}
        </div>
      )}

      {modal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9500,
            background: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div style={{ width: "min(560px, 100%)" }}>
            <BroadcastCard
              broadcast={modal}
              language={language}
              theme={theme}
              variant="modal"
              onDismiss={() => dismiss(modal)}
              onAck={() => ack(modal)}
              onClose={() => hideLocally(modal)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BroadcastCenter;
